import React, { useState, useEffect, useRef } from 'react';
import { User } from 'lucide-react';
import { message } from 'antd';
import { useAuth } from '../../contexts/AuthContext';
import { useAvatar } from '../../contexts/AvatarContext';
import { usersService } from '../../services/users.service';
import { departmentsService } from '../../services/departments.service';
import { buildFullUrl } from '../../contexts/AvatarContext';
import { loadAvatarFromData } from '../../utils/avatarLoader';
import { formatRole } from '../../utils/formatters';
import type { UserDetail } from '@shared/types';
import { getUserFriendlyErrorMessage } from '@shared/services';
import backgroundProfile from '../../assets/icon-profile/background.svg';
import closeIcon from '../../assets/icon-profile/close.svg';
import cameraIcon from '../../assets/icon-profile/camera.svg';
import mailIcon from '../../assets/icon-profile/mail.svg';
import warehouseIcon from '../../assets/icon-profile/warehouse.svg';
import briefcaseIcon from '../../assets/icon-profile/briefcase-business.svg';
import squareUserIcon from '../../assets/icon-profile/square-user.svg';
import ellipseIcon from '../../assets/icon-profile/Ellipse.svg';
import styles from './ProfileSidebar.module.css';

interface ProfileSidebarProps {
  visible: boolean;
  onClose: () => void;
}

export const ProfileSidebar: React.FC<ProfileSidebarProps> = ({ visible, onClose }) => {
  const { user } = useAuth();
  const { avatarUri, setAvatarUri, refreshAvatar } = useAvatar();
  const [userDetail, setUserDetail] = useState<UserDetail | null>(null);
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [departmentName, setDepartmentName] = useState<string>('Chưa xác định');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (visible && user?.id) {
      fetchUserDetail();
    }
  }, [visible, user?.id]);

  // Disable body scroll when sidebar is open
  useEffect(() => {
    if (visible) {
      // Save current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Restore scroll position when closing
        const savedScrollY = document.body.style.top;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        if (savedScrollY) {
          const scrollValue = parseInt(savedScrollY.replace('px', '').replace('-', ''), 10);
          window.scrollTo(0, scrollValue);
        }
      };
    }
  }, [visible]);

  useEffect(() => {
    const loadAvatar = async () => {
      if (avatarUri) {
        setProfileImage(avatarUri);
      } else if (userDetail?.avatar) {
        const loadedAvatar = await loadAvatarFromData(userDetail.avatar);
        if (loadedAvatar) {
          setProfileImage(loadedAvatar);
        }
      }
    };
    loadAvatar();
  }, [avatarUri, userDetail]);

  const fetchUserDetail = async () => {
    if (!user?.id) return;
    
    try {
      const userId = parseInt(user.id.toString(), 10);
      if (!isNaN(userId)) {
        const detail = await usersService.getUserById(userId);
        setUserDetail(detail);
        
        // Load avatar
        const avatarUri = await loadAvatarFromData(detail.avatar);
        if (avatarUri) {
          setProfileImage(avatarUri);
        }

        // Fetch department name
        if (detail.departmentId) {
          try {
            const department = await departmentsService.getDepartmentById(detail.departmentId);
            setDepartmentName(department.name);
          } catch (error) {
            console.error('Error fetching department:', error);
            setDepartmentName('Chưa xác định');
          }
        } else {
          setDepartmentName('Chưa xác định');
        }
      }
    } catch (error) {
      console.error('Error fetching user detail:', error);
    }
  };



  const handleCameraClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      message.error('Vui lòng chọn file ảnh hợp lệ.');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      message.error('Kích thước file không được vượt quá 5MB.');
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfileImage(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Upload to server
    if (!user?.id) {
      message.error('Không tìm thấy thông tin người dùng.');
      return;
    }

    setUploading(true);
    try {
      const response = await usersService.uploadAvatar(parseInt(user.id.toString()), file);
      
      if (response.success && response.data) {
        // Get updated profile from server to get full avatar URL
        try {
          const profile = await usersService.getProfile();
          let finalAvatarUrl: string | null = null;
          
          if (profile?.avatar) {
            finalAvatarUrl = buildFullUrl(profile.avatar);
          } else if (response.data.url) {
            finalAvatarUrl = buildFullUrl(response.data.url);
          } else if (response.data.relativeUrl) {
            finalAvatarUrl = buildFullUrl(response.data.relativeUrl);
          }

          if (finalAvatarUrl) {
            await setAvatarUri(finalAvatarUrl);
            setProfileImage(finalAvatarUrl);
            // Refresh user detail to get updated avatar
            await fetchUserDetail();
            // Refresh avatar context
            await refreshAvatar();
            message.success('Đã cập nhật ảnh đại diện thành công.');
          } else {
            message.warning('Đã upload nhưng không thể lấy URL avatar từ server.');
          }
        } catch (profileError) {
          if (response.data.url) {
            const fallbackUrl = buildFullUrl(response.data.url);
            if (fallbackUrl) {
              await setAvatarUri(fallbackUrl);
              setProfileImage(fallbackUrl);
              await fetchUserDetail();
              await refreshAvatar();
            }
          }
          message.success('Đã cập nhật ảnh đại diện thành công.');
        }
      } else {
        throw new Error(response.message || 'Upload thất bại');
      }
    } catch (error: any) {
      message.error(
        getUserFriendlyErrorMessage(
          error,
          'Không thể cập nhật ảnh đại diện. Vui lòng thử lại.',
        ),
      );
      // Revert to previous image on error
      if (avatarUri) {
        setProfileImage(avatarUri);
      } else if (userDetail?.avatar) {
        const avatarUrl = buildFullUrl(userDetail.avatar);
        if (avatarUrl) {
          setProfileImage(avatarUrl);
        }
      }
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (!visible) return null;

  return (
    <>
      <div className={styles.overlay} onClick={onClose} />
      <div 
        className={`${styles.sidebar} ${visible ? styles.visible : ''}`}
        style={{ backgroundImage: `url(${backgroundProfile})` }}
      >
        <div className={styles.sidebarContent}>
          {/* Close button */}
          <button className={styles.closeButton} onClick={onClose}>
            <img src={closeIcon} alt="Close" className={styles.closeIcon} />
          </button>

          {/* Profile section */}
          <div className={styles.profileSection}>
            {/* Avatar with camera icon */}
            <div className={styles.avatarContainer}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleFileChange}
                disabled={uploading}
              />
              {profileImage ? (
                <img 
                  src={profileImage} 
                  alt="Profile" 
                  className={styles.avatar}
                  onError={() => setProfileImage(null)}
                />
              ) : (
                <div className={styles.avatarPlaceholder}>
                  <User size={40} />
                </div>
              )}
              <div 
                className={styles.cameraIcon}
                onClick={handleCameraClick}
                style={{ cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.6 : 1 }}
              >
                <img src={cameraIcon} alt="Camera" className={styles.cameraIconImg} />
              </div>
              {uploading && (
                <div className={styles.uploadingOverlay}>
                  <div className={styles.uploadingSpinner}></div>
                </div>
              )}
            </div>

            {/* User name */}
            <h2 className={styles.userName}>
              {userDetail?.fullName || user?.name || 'Người dùng'}
            </h2>

            {/* Basic Information Section */}
            <div className={styles.infoSection}>
              <div className={styles.sectionHeader}>
                <img src={ellipseIcon} alt="" className={styles.bullet} />
                <span className={styles.sectionTitle}>Thông tin cơ bản</span>
              </div>

              <div className={styles.infoList}>
                {/* Email */}
                <div className={styles.infoItem}>
                  <img src={mailIcon} alt="Email" className={styles.infoIcon} />
                  <span className={styles.infoText}>
                    {userDetail?.email || user?.email || 'Chưa có email'}
                  </span>
                </div>

                {/* Department */}
                <div className={styles.infoItem}>
                  <img src={warehouseIcon} alt="Department" className={styles.infoIcon} />
                  <span className={styles.infoText}>
                    {departmentName}
                  </span>
                </div>

                {/* Position */}
                <div className={styles.infoItem}>
                  <img src={briefcaseIcon} alt="Position" className={styles.infoIcon} />
                  <span className={styles.infoText}>
                    {userDetail?.position || 'Chưa xác định'}
                  </span>
                </div>

                {/* Role */}
                <div className={styles.infoItem}>
                  <img src={squareUserIcon} alt="Role" className={styles.infoIcon} />
                  <span className={styles.infoText}>
                    {formatRole(userDetail?.role || user?.role) || 'Nhân viên'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Wave decoration at bottom */}
          <div className={styles.waveDecoration} />
        </div>
      </div>
    </>
  );
};
